import { UNKNOWN_PLAYER_ID } from '@/constants';
import {
  FIXTURE_ACTION_SUBTYPE,
  FIXTURE_ACTION_TYPE,
} from '@/service/constants';
import {
  ActionType,
  FixtureAction,
  FixtureConfig,
  FixtureSummary,
  MatchSummaryTeam,
  Player,
} from '@/service/types';
import { getPlayer } from '@/components/FixtureTabs/helpers/getPlayerNameAndNumber';
import {
  EditActionState,
  GeneratePlayersForOptionsProps,
  EditActionPlayerOptions,
} from './types';

interface GenerateInitialStateProps {
  fixtureSummary: FixtureSummary | null;
  action: FixtureAction;
  fixtureConfig?: FixtureConfig;
}

interface isEditActionSaveButtonDisabledProps {
  editedAction: EditActionState;
  action: FixtureAction;
  isPlayerRequired: boolean;
  isChangeableActionTypeId: boolean;
}

export const generateInitialState = ({
  fixtureSummary,
  action,
  fixtureConfig,
}: GenerateInitialStateProps) => {
  const generateActionTypeOptions = () => {
    const defaultAction = fixtureConfig?.fixtureOptions?.actionButtons.find(
      (actionButton) =>
        actionButton.actionType.id === action.fixtureActionTypeId
    );

    if (defaultAction?.changeableActionTypeIds?.length) {
      const relatedActions =
        fixtureConfig?.fixtureOptions?.actionButtons.filter((actionButton) =>
          defaultAction?.changeableActionTypeIds?.includes(
            actionButton.actionType.id
          )
        );
      return [
        defaultAction.actionType,
        ...(relatedActions?.map(
          (relatedAction) => relatedAction.actionType
        ) as ActionType[]),
      ];
    }

    return [defaultAction?.actionType] as ActionType[];
  };

  const additionalActionTypeOptions = generateActionTypeOptions();

  const actionType = additionalActionTypeOptions[0];

  const actionSubTypes = actionType?.subTypes as ActionType[];

  return {
    fixtureActionType: !actionType
      ? null
      : {
          ...actionType,
          subTypes: actionSubTypes,
        },
    fixtureSubActionType:
      actionType?.subTypes?.find(
        (subType) => subType?.id === action.fixtureActionSubTypeId
      ) || null,
    teamId: action.teamId,
    player: getPlayer(action.playerId, fixtureConfig, fixtureSummary) as Player,
    additionalActionTypeOptions,
  };
};

export const actionsReducer = (
  state: EditActionState,
  newValue: Partial<EditActionState>
): EditActionState => {
  return { ...state, ...newValue };
};

export const generateEditActionTeam = (
  action: FixtureAction,
  newAction: EditActionState,
  homeTeam: MatchSummaryTeam,
  awayTeam: MatchSummaryTeam
) => {
  const isEditedActionSubTypeOwnGoal =
    newAction.fixtureSubActionType?.id === FIXTURE_ACTION_SUBTYPE.OWN_GOAL;
  const isHomeTeam = action.teamId === homeTeam.id;

  if (isEditedActionSubTypeOwnGoal) {
    return isHomeTeam ? awayTeam : homeTeam;
  }

  return isHomeTeam ? homeTeam : awayTeam;
};

export const generatePlayersForOptions = ({
  editedAction,
  fixtureSummary,
  action,
}: GeneratePlayersForOptionsProps) => {
  if (!fixtureSummary) return [];
  const { awayTeam, homeTeam } = fixtureSummary;

  const team = generateEditActionTeam(action, editedAction, homeTeam, awayTeam);

  return {
    teamId: team.id,
    teamName: team.teamName,
    players: [
      ...team.players,
      {
        firstName: '',
        fullName: 'Unknown',
        id: UNKNOWN_PLAYER_ID,
        isInCurrentLineup: true,
        isStartingSquad: false,
        lastName: '',
        shirtNumber: '',
      },
    ].sort(
      (a, b) =>
        Number(b.isInCurrentLineup) - Number(a.isInCurrentLineup) ||
        parseInt(a.shirtNumber) - parseInt(b.shirtNumber)
    ),
  } as EditActionPlayerOptions;
};

export const isEditActionSaveButtonDisabled = ({
  editedAction,
  action,
  isPlayerRequired,
  isChangeableActionTypeId,
}: isEditActionSaveButtonDisabledProps) => {
  if (
    !editedAction.fixtureActionType ||
    (isPlayerRequired && !editedAction.player)
  ) {
    return true;
  }

  if (
    isPlayerRequired &&
    (!editedAction.player || editedAction.player.id === action.playerId)
  ) {
    if (!isChangeableActionTypeId) {
      return true;
    }

    if (isChangeableActionTypeId) {
      if (action.fixtureActionTypeId === editedAction.fixtureActionType?.id) {
        if (!editedAction.fixtureActionType?.subTypes?.length) {
          return true;
        }

        if (
          editedAction.fixtureActionType?.subTypes?.length &&
          action.fixtureActionSubTypeId ===
            editedAction.fixtureSubActionType?.id
        ) {
          return true;
        }
      }
      if (
        action.fixtureActionTypeId === FIXTURE_ACTION_TYPE.GOAL &&
        (action.fixtureActionSubTypeId
          ? action.fixtureActionSubTypeId ===
            editedAction.fixtureSubActionType?.id
          : action.fixtureActionSubTypeId === editedAction.fixtureSubActionType)
      ) {
        return true;
      }
    }
  }

  if (
    !editedAction.fixtureSubActionType &&
    editedAction.fixtureActionType?.subTypes?.length &&
    action.fixtureActionTypeId !== FIXTURE_ACTION_TYPE.GOAL
  ) {
    return true;
  }

  return (
    isChangeableActionTypeId &&
    action.fixtureActionTypeId === editedAction.fixtureActionType?.id &&
    !isPlayerRequired
  );
};
